import { useDebounceCallback } from "usehooks-ts";

import { Fragment, useCallback, useRef } from "react";
import { JSX } from "react";

import {
  useConfigurationApi,
  useConfigurationEvent,
  useCsAttributeById,
  useCsConfigurationApi,
  useSessionId,
} from "@plansoft-configuration-renderer/framework";

import { useCadStatus } from "./customContext/CadStatusContext";

interface IFormattedAssemblyDataInstance {
  [key: string]: unknown;
  product: {
    [key: string]: unknown;
    parameters: {
      question: string;
      value?: string;
    }[];
  };
}
interface IFormattedAssemblyDataInstances {
  instance: IFormattedAssemblyDataInstance;
}
interface IFormattedAssemblyData {
  instances: IFormattedAssemblyDataInstances[];
}

/**
 * Demo on how to access internal state
 * @returns
 */
export function ConfigurationStateSpy(): JSX.Element {
  const sessionId = useSessionId();
  const configurationApi = useConfigurationApi();
  const { setIsCadLoading } = useCadStatus();
  const attProductView = useCsAttributeById("XAS_PRODUCT_VIEW");
  const { invokeUserDefinedFunction } = useCsConfigurationApi();
  const timestampRef = useRef(0);
  const timestampRefOld = useRef(0);
  const cadTimestamp =
    attProductView?.configurations?.[0]?.properties?.CAD_TIMESTAMP?.value;

  timestampRef.current = cadTimestamp;

  const handleCadBoundingBoxInformation = useCallback(async () => {
    let cadData;
    //console.log("id", sessionId);
    setIsCadLoading(true);
    try {
      //console.log("1-------");
      let assemblyData = await configurationApi.getCadClickAssembly({
        id: sessionId,
      });
      //console.log("assemblyData", assemblyData);

      const formattedAssemblyData: IFormattedAssemblyData = {
        instances: [],
      };
      if (assemblyData.instances) {
        // @ts-expect-error: API result and type declaration is broken, here we force it into a new structure.
        formattedAssemblyData.instances = assemblyData.instances.map((data) => {
          const instanceData = { ...data };
          if (instanceData?.product?.parameters) {
            const cleanedParams = instanceData.product?.parameters.map(
              (param) => {
                const newParam = { value: "", ...param };

                return newParam;
              },
            );

            instanceData.product.parameters = cleanedParams;
          }
          return { instance: instanceData };
        });
      }

      //console.log("2-------");
      const cadResponse = await fetch(
        "https://ccwebapi11.cadclick.com/ccapi/v1/assembly/bounding-box",
        {
          method: "POST",
          headers: {
            "x-api-key": "weg_austria",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formattedAssemblyData),
        },
      );
      cadData = await cadResponse.json();

      //console.log("cad data", cadData);

      //console.log("3-------");

      const inputObj = {
        width: (cadData[0] - cadData[3]) * -1,
        depth: (cadData[1] - cadData[4]) * -1,
        height: (cadData[2] - cadData[5]) * -1,
      };

      const outputUpdateCADBoundingBox = await invokeUserDefinedFunction({
        name: "updateCADBoundingBox",
        input: JSON.stringify(inputObj),
        path: "/XAS_PRODUCT_VIEW/0",
      });
      console.log(`invokeUserDefinedFunction "updateCADBoundingBox":`, {
        inputObj,
        outputUpdateCADBoundingBox,
      });

      // console.log("4------- finish");
    } catch (error) {
      console.warn(error);
    }
    setIsCadLoading(false);
  }, [configurationApi, sessionId, invokeUserDefinedFunction, setIsCadLoading]);

  const onConfigurationEvent = useDebounceCallback(() => {
    if (
      timestampRef.current !== null &&
      timestampRef.current !== timestampRefOld.current
    ) {
      timestampRefOld.current = timestampRef.current;
      handleCadBoundingBoxInformation();
    }
  }, 0);

  useConfigurationEvent({
    onConfigurationEvent: onConfigurationEvent,
  });

  return <Fragment />;
}
